import React, { useState, useEffect } from 'react';
import { mockProducts, mockQuotes, mockOrders, mockDonations, subscribeToDB } from '../services/mockDatabase';
import { Plus, ListFilter, DollarSign, Package, MessageSquare, Award, Trash2, Pencil, X, Upload } from 'lucide-react';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('inventory'); // 'inventory' | 'orders' | 'quotes' | 'donations'

  // DB States
  const [productsList, setProductsList] = useState([]);
  const [quotesList, setQuotesList] = useState([]);
  const [ordersList, setOrdersList] = useState([]);
  const [donationStats, setDonationStats] = useState({ totalDonated: 0, goalAmount: 1000, donationPercentage: 0.1 });

  // Product editing state
  const [editingProduct, setEditingProduct] = useState(null); // holds product object if editing, else null

  // Form inputs for new product
  const [newProduct, setNewProduct] = useState({
    name: '',
    description: '',
    price: '',
    category: '',
    imageUrl: '',
    stock: '',
    isCustomizable: false
  });

  // Quote pricing helper
  const [quotePrices, setQuotePrices] = useState({});

  useEffect(() => {
    // Initial fetch
    setProductsList(mockProducts.getProducts());
    setQuotesList(mockQuotes.getQuotes());
    setOrdersList(mockOrders.getOrders());
    setDonationStats(mockDonations.getDonationStats());

    // Subscribe to DB changes
    const unsubscribe = subscribeToDB(() => {
      setProductsList(mockProducts.getProducts());
      setQuotesList(mockQuotes.getQuotes());
      setOrdersList(mockOrders.getOrders());
      setDonationStats(mockDonations.getDonationStats());
    });

    return unsubscribe;
  }, []);

  // Image Upload File Handler
  const handleImageFileChange = (e, isEdit = false) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate size (limit base64 size to prevent localStorage overflow, ~1.5MB max)
    if (file.size > 1500000) {
      alert('Selected file is too large! Please choose an image smaller than 1.5MB.');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      if (isEdit) {
        setEditingProduct(prev => ({ ...prev, imageUrl: reader.result }));
      } else {
        setNewProduct(prev => ({ ...prev, imageUrl: reader.result }));
      }
    };
    reader.readAsDataURL(file);
  };

  // Add Product handler
  const handleAddProduct = (e) => {
    e.preventDefault();
    if (!newProduct.name || !newProduct.price) return;

    mockProducts.addProduct({
      name: newProduct.name,
      description: newProduct.description,
      price: parseFloat(newProduct.price),
      category: newProduct.category || 'Uncategorized',
      imageUrl: newProduct.imageUrl || 'https://images.unsplash.com/photo-1590076214667-c06f2348b6bb?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.0.3',
      stock: parseInt(newProduct.stock) || 0,
      isCustomizable: newProduct.isCustomizable
    });

    // Reset Form
    setNewProduct({
      name: '',
      description: '',
      price: '',
      category: '',
      imageUrl: '',
      stock: '',
      isCustomizable: false
    });
  };

  // Edit Product Form Trigger
  const handleStartEdit = (product) => {
    setEditingProduct({ ...product });
    // Scroll form into view for mobile devices
    window.scrollTo({ top: 200, behavior: 'smooth' });
  };

  // Save/Submit Edit Product handler
  const handleSaveEdit = (e) => {
    e.preventDefault();
    if (!editingProduct.name || !editingProduct.price) return;

    mockProducts.updateProduct({
      ...editingProduct,
      price: parseFloat(editingProduct.price),
      stock: parseInt(editingProduct.stock) || 0
    });

    setEditingProduct(null);
  };

  // Cancel Edit
  const handleCancelEdit = () => {
    setEditingProduct(null);
  };

  // Delete Product
  const handleDeleteProduct = (id) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      // If we are currently editing the deleted item, cancel editing
      if (editingProduct && editingProduct.id === id) {
        setEditingProduct(null);
      }
      mockProducts.deleteProduct(id);
    }
  };

  // Quote Pricing Submit
  const handleQuotePrice = (quoteId) => {
    const price = quotePrices[quoteId];
    if (!price || isNaN(price)) {
      alert('Please enter a valid price for the quote.');
      return;
    }
    mockQuotes.updateQuoteStatus(quoteId, 'quoted', price);
  };

  // Update Order Status
  const handleUpdateOrderStatus = (orderId, newStatus) => {
    const updatedOrders = ordersList.map(o => o.id === orderId ? { ...o, status: newStatus } : o);
    localStorage.setItem('bb_orders', JSON.stringify(updatedOrders));
    setOrdersList(updatedOrders);
  };

  // Update Donation Goals
  const handleUpdateDonations = (e) => {
    e.preventDefault();
    mockDonations.updateDonationStats({
      goalAmount: parseFloat(donationStats.goalAmount),
      donationPercentage: parseFloat(donationStats.donationPercentage)
    });
    alert('Donation settings updated successfully!');
  };

  return (
    <div className="max-w-6xl mx-auto my-12 px-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h2 className="text-3xl font-extrabold text-gray-800">Owner Dashboard</h2>
          <p className="text-gray-500">Welcome Georgia! Manage inventory, quotes, and donations here.</p>
        </div>
      </div>

      {/* Admin Navigation Tabs */}
      <div className="flex border-b border-gray-200 mb-8 overflow-x-auto gap-2">
        <button
          onClick={() => setActiveTab('inventory')}
          className={`flex items-center gap-2 px-5 py-3 border-b-2 font-bold text-sm transition-all shrink-0 ${
            activeTab === 'inventory' 
              ? 'border-pink-500 text-pink-600' 
              : 'border-transparent text-gray-400 hover:text-gray-600'
          }`}
        >
          <Package size={16} /> Inventory & Categorization
        </button>
        <button
          onClick={() => setActiveTab('orders')}
          className={`flex items-center gap-2 px-5 py-3 border-b-2 font-bold text-sm transition-all shrink-0 ${
            activeTab === 'orders' 
              ? 'border-pink-500 text-pink-600' 
              : 'border-transparent text-gray-400 hover:text-gray-600'
          }`}
        >
          <Plus size={16} /> Orders Tracker ({ordersList.length})
        </button>
        <button
          onClick={() => setActiveTab('quotes')}
          className={`flex items-center gap-2 px-5 py-3 border-b-2 font-bold text-sm transition-all shrink-0 ${
            activeTab === 'quotes' 
              ? 'border-pink-500 text-pink-600' 
              : 'border-transparent text-gray-400 hover:text-gray-600'
          }`}
        >
          <MessageSquare size={16} /> Custom Quotes ({quotesList.filter(q => q.status === 'pending').length})
        </button>
        <button
          onClick={() => setActiveTab('donations')}
          className={`flex items-center gap-2 px-5 py-3 border-b-2 font-bold text-sm transition-all shrink-0 ${
            activeTab === 'donations' 
              ? 'border-pink-500 text-pink-600' 
              : 'border-transparent text-gray-400 hover:text-gray-600'
          }`}
        >
          <Award size={16} /> St. Jude Settings
        </button>
      </div>

      {/* Tab Panels */}
      <div>
        
        {/* Tab 1: Inventory & Categorization */}
        {activeTab === 'inventory' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            
            {/* Left side: Add / Edit Product Form */}
            <div className="bg-white p-6 rounded-3xl shadow-md border border-pink-50 h-fit transition-all duration-300">
              {editingProduct ? (
                // Edit Product Mode
                <>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-bold text-gray-800 text-lg flex items-center gap-2">
                      <Pencil className="text-purple-500" size={18} /> Edit Product
                    </h3>
                    <button 
                      onClick={handleCancelEdit}
                      className="p-1.5 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-600 transition"
                      title="Cancel Edit"
                    >
                      <X size={16} />
                    </button>
                  </div>
                  <form onSubmit={handleSaveEdit} className="space-y-4">
                    <div>
                      <label className="block text-xs font-bold text-gray-500 mb-1">Product Name</label>
                      <input
                        type="text"
                        required
                        value={editingProduct.name}
                        onChange={(e) => setEditingProduct({ ...editingProduct, name: e.target.value })}
                        placeholder="e.g. Rainbow Lizard"
                        className="w-full p-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 mb-1">Description</label>
                      <textarea
                        rows={2}
                        value={editingProduct.description}
                        onChange={(e) => setEditingProduct({ ...editingProduct, description: e.target.value })}
                        placeholder="Describe the colors and beads..."
                        className="w-full p-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-bold text-gray-500 mb-1">Price ($)</label>
                        <input
                          type="number"
                          step="0.01"
                          required
                          value={editingProduct.price}
                          onChange={(e) => setEditingProduct({ ...editingProduct, price: e.target.value })}
                          placeholder="5.00"
                          className="w-full p-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-500 mb-1">Stock</label>
                        <input
                          type="number"
                          value={editingProduct.stock}
                          onChange={(e) => setEditingProduct({ ...editingProduct, stock: e.target.value })}
                          placeholder="10"
                          className="w-full p-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 mb-1">Category</label>
                      <input
                        type="text"
                        value={editingProduct.category}
                        onChange={(e) => setEditingProduct({ ...editingProduct, category: e.target.value })}
                        placeholder="e.g. Bead Animals"
                        className="w-full p-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    
                    {/* File Upload Selector */}
                    <div>
                      <label className="block text-xs font-bold text-gray-500 mb-1 flex items-center gap-1">
                        <Upload size={12} /> Upload Product Image
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageFileChange(e, true)}
                        className="w-full text-xs text-gray-500 file:mr-3 file:py-2 file:px-3 file:rounded-xl file:border-0 file:text-xs file:font-bold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100 cursor-pointer"
                      />
                      {editingProduct.imageUrl && (
                        <div className="mt-2 relative w-16 h-16 rounded-lg overflow-hidden border border-gray-200">
                          <img src={editingProduct.imageUrl} alt="preview" className="w-full h-full object-cover" />
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-2 pt-2">
                      <input
                        type="checkbox"
                        id="isCustomizableEdit"
                        checked={editingProduct.isCustomizable}
                        onChange={(e) => setEditingProduct({ ...editingProduct, isCustomizable: e.target.checked })}
                        className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      />
                      <label htmlFor="isCustomizableEdit" className="text-xs font-bold text-gray-600">This item can be customized</label>
                    </div>
                    <div className="flex gap-2 pt-2">
                      <button
                        type="button"
                        onClick={handleCancelEdit}
                        className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold py-2.5 rounded-xl transition text-sm"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="flex-1 bg-purple-600 hover:bg-purple-700 text-white font-bold py-2.5 rounded-xl transition text-sm"
                      >
                        Save Changes
                      </button>
                    </div>
                  </form>
                </>
              ) : (
                // Add Product Mode
                <>
                  <h3 className="font-bold text-gray-800 text-lg mb-4 flex items-center gap-2">
                    <Plus className="text-pink-500" /> Add New Inventory Item
                  </h3>
                  <form onSubmit={handleAddProduct} className="space-y-4">
                    <div>
                      <label className="block text-xs font-bold text-gray-500 mb-1">Product Name</label>
                      <input
                        type="text"
                        required
                        value={newProduct.name}
                        onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                        placeholder="e.g. Rainbow Lizard"
                        className="w-full p-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 mb-1">Description</label>
                      <textarea
                        rows={2}
                        value={newProduct.description}
                        onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
                        placeholder="Describe the colors and beads used..."
                        className="w-full p-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-bold text-gray-500 mb-1">Price ($)</label>
                        <input
                          type="number"
                          step="0.01"
                          required
                          value={newProduct.price}
                          onChange={(e) => setNewProduct({ ...newProduct, price: e.target.value })}
                          placeholder="5.00"
                          className="w-full p-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-500 mb-1">Stock</label>
                        <input
                          type="number"
                          value={newProduct.stock}
                          onChange={(e) => setNewProduct({ ...newProduct, stock: e.target.value })}
                          placeholder="10"
                          className="w-full p-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 mb-1">Category</label>
                      <input
                        type="text"
                        value={newProduct.category}
                        onChange={(e) => setNewProduct({ ...newProduct, category: e.target.value })}
                        placeholder="e.g. Bead Animals"
                        className="w-full p-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                      />
                    </div>

                    {/* File Upload Selector */}
                    <div>
                      <label className="block text-xs font-bold text-gray-500 mb-1 flex items-center gap-1">
                        <Upload size={12} /> Upload Product Image
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageFileChange(e, false)}
                        className="w-full text-xs text-gray-500 file:mr-3 file:py-2 file:px-3 file:rounded-xl file:border-0 file:text-xs file:font-bold file:bg-pink-50 file:text-pink-700 hover:file:bg-pink-100 cursor-pointer"
                      />
                      {newProduct.imageUrl && (
                        <div className="mt-2 relative w-16 h-16 rounded-lg overflow-hidden border border-gray-200">
                          <img src={newProduct.imageUrl} alt="preview" className="w-full h-full object-cover" />
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-2 pt-2">
                      <input
                        type="checkbox"
                        id="isCustomizable"
                        checked={newProduct.isCustomizable}
                        onChange={(e) => setNewProduct({ ...newProduct, isCustomizable: e.target.checked })}
                        className="w-4 h-4 text-pink-600 border-gray-300 rounded focus:ring-pink-500"
                      />
                      <label htmlFor="isCustomizable" className="text-xs font-bold text-gray-600">This item can be customized</label>
                    </div>
                    <button
                      type="submit"
                      className="w-full bg-pink-500 hover:bg-pink-600 text-white font-bold py-3 rounded-xl transition text-sm"
                    >
                      Save Item to Shop
                    </button>
                  </form>
                </>
              )}
            </div>

            {/* Right side: Catalog List */}
            <div className="lg:col-span-2 space-y-4">
              <h3 className="font-bold text-gray-800 text-lg flex items-center gap-2">
                <ListFilter className="text-pink-500" /> Active Shop Catalog ({productsList.length})
              </h3>
              <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-pink-50/50 text-gray-500 font-bold uppercase text-xs">
                      <tr>
                        <th className="p-4">Item</th>
                        <th className="p-4">Category</th>
                        <th className="p-4 text-right">Price</th>
                        <th className="p-4 text-center">Stock</th>
                        <th className="p-4 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 font-semibold text-gray-700">
                      {productsList.map(prod => (
                        <tr key={prod.id} className="hover:bg-pink-50/10">
                          <td className="p-4 flex items-center gap-3">
                            <div className="w-10 h-10 bg-gray-100 rounded-lg overflow-hidden shrink-0 border border-gray-200">
                              {prod.imageUrl && <img src={prod.imageUrl} alt={prod.name} className="w-full h-full object-cover" />}
                            </div>
                            <div>
                              <span className="block font-bold text-gray-800">{prod.name}</span>
                              {prod.isCustomizable && <span className="text-[10px] bg-yellow-100 text-yellow-800 px-1.5 py-0.5 rounded-full font-bold">Customizable</span>}
                            </div>
                          </td>
                          <td className="p-4">
                            <span className="bg-gray-100 text-gray-600 text-xs px-2.5 py-1 rounded-full font-medium">{prod.category}</span>
                          </td>
                          <td className="p-4 text-right font-bold text-pink-600">${prod.price.toFixed(2)}</td>
                          <td className="p-4 text-center">{prod.stock}</td>
                          <td className="p-4 text-center">
                            <div className="flex justify-center gap-2">
                              <button
                                onClick={() => handleStartEdit(prod)}
                                className="text-gray-400 hover:text-purple-600 transition-colors p-1"
                                title="Edit Item"
                              >
                                <Pencil size={16} />
                              </button>
                              <button
                                onClick={() => handleDeleteProduct(prod.id)}
                                className="text-gray-400 hover:text-red-500 transition-colors p-1"
                                title="Delete Item"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tab 2: Orders Tracker */}
        {activeTab === 'orders' && (
          <div className="space-y-6">
            <h3 className="font-bold text-gray-800 text-lg flex items-center gap-2">
              <Package className="text-pink-500" /> Customer Sales & Deliveries ({ordersList.length})
            </h3>
            {ordersList.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-3xl border border-gray-100 text-gray-400 italic">No orders received yet.</div>
            ) : (
              <div className="space-y-4">
                {ordersList.map(order => (
                  <div key={order.id} className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm flex flex-col md:flex-row justify-between gap-4">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs uppercase text-gray-400 font-bold">Order ID</span>
                        <span className="font-mono font-bold text-gray-800">{order.id}</span>
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                          order.status === 'delivered' ? 'bg-green-100 text-green-700' :
                          order.status === 'shipped' ? 'bg-blue-100 text-blue-700' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {order.status.toUpperCase()}
                        </span>
                      </div>
                      <div className="text-sm font-semibold text-gray-700">
                        <span>Customer: {order.customerName} ({order.customerEmail})</span>
                      </div>
                      <div className="text-xs text-gray-500 space-y-1">
                        <span className="block font-bold">Items Purchased:</span>
                        {order.items.map((item, idx) => (
                          <span key={idx} className="block pl-2 text-gray-600">
                            • {item.quantity}x {item.name} (${item.price.toFixed(2)} ea)
                            {item.customOptions && item.customOptions.letters && (
                              <span className="text-purple-600 font-bold ml-1">[{item.customOptions.letters.toUpperCase()}]</span>
                            )}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="flex flex-col md:items-end justify-between gap-3 text-right">
                      <div>
                        <span className="block text-xs text-gray-400 font-bold">Order Total</span>
                        <span className="text-pink-600 font-extrabold text-xl">${order.totalAmount.toFixed(2)}</span>
                      </div>
                      <div>
                        <span className="block text-xs text-gray-400 font-bold mb-1">Update Status</span>
                        <div className="flex gap-1.5">
                          <button
                            onClick={() => handleUpdateOrderStatus(order.id, 'processing')}
                            className="bg-yellow-50 hover:bg-yellow-100 text-yellow-700 text-xs px-3 py-1.5 rounded-lg font-bold transition"
                          >
                            Processing
                          </button>
                          <button
                            onClick={() => handleUpdateOrderStatus(order.id, 'shipped')}
                            className="bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs px-3 py-1.5 rounded-lg font-bold transition"
                          >
                            Ship It
                          </button>
                          <button
                            onClick={() => handleUpdateOrderStatus(order.id, 'delivered')}
                            className="bg-green-50 hover:bg-green-100 text-green-700 text-xs px-3 py-1.5 rounded-lg font-bold transition"
                          >
                            Deliver
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Tab 3: Custom Quotes Reviews */}
        {activeTab === 'quotes' && (
          <div className="space-y-6">
            <h3 className="font-bold text-gray-800 text-lg flex items-center gap-2">
              <MessageSquare className="text-pink-500" /> Customers Custom Requests ({quotesList.length})
            </h3>
            {quotesList.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-3xl border border-gray-100 text-gray-400 italic">No custom requests submitted yet.</div>
            ) : (
              <div className="space-y-4">
                {quotesList.map(quote => (
                  <div key={quote.id} className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm flex flex-col md:flex-row justify-between gap-6">
                    <div className="flex-1 space-y-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs uppercase text-gray-400 font-bold">Quote ID</span>
                        <span className="font-mono font-bold text-gray-800">{quote.id}</span>
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                          quote.status === 'approved' ? 'bg-green-100 text-green-700' :
                          quote.status === 'quoted' ? 'bg-blue-100 text-blue-700' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {quote.status.toUpperCase()}
                        </span>
                      </div>
                      <div className="text-sm">
                        <span className="block font-bold text-gray-700">Description:</span>
                        <p className="text-gray-600 bg-gray-50 p-3 rounded-xl border border-gray-100 mt-1 italic">"{quote.description}"</p>
                      </div>
                      
                      {/* Quote parameters preview */}
                      <div className="flex gap-4 text-xs font-semibold text-gray-500">
                        {quote.customText && (
                          <div>Letters: <span className="text-purple-600 font-bold">{quote.customText.toUpperCase()}</span></div>
                        )}
                        {quote.customColors && quote.customColors.length > 0 && (
                          <div>Colors: <span className="text-gray-800">{quote.customColors.join(', ')}</span></div>
                        )}
                      </div>
                      
                      <div className="text-xs text-gray-400">
                        Requested by: <span className="font-semibold">{quote.customerName} ({quote.customerEmail})</span>
                      </div>
                    </div>

                    <div className="w-full md:w-56 flex flex-col justify-between gap-3 text-right">
                      {quote.quotedPrice && (
                        <div>
                          <span className="block text-xs text-gray-400 font-bold">Quoted Price</span>
                          <span className="text-purple-600 font-extrabold text-xl">${quote.quotedPrice.toFixed(2)}</span>
                        </div>
                      )}
                      
                      {quote.status === 'pending' && (
                        <div className="bg-pink-50/30 p-3 rounded-2xl border border-pink-100 space-y-2 mt-auto">
                          <label className="block text-left text-xs font-bold text-gray-500 mb-1">Set Price Quote ($)</label>
                          <div className="flex gap-2">
                            <input
                              type="number"
                              step="0.01"
                              placeholder="10.00"
                              value={quotePrices[quote.id] || ''}
                              onChange={(e) => setQuotePrices({ ...quotePrices, [quote.id]: e.target.value })}
                              className="w-full p-2 border border-gray-200 rounded-lg text-sm font-bold text-center"
                            />
                            <button
                              onClick={() => handleQuotePrice(quote.id)}
                              className="bg-pink-500 hover:bg-pink-600 text-white text-xs px-3 rounded-lg font-bold transition shrink-0"
                            >
                              Submit
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Tab 4: St. Jude Settings */}
        {activeTab === 'donations' && (
          <div className="max-w-md bg-white p-6 md:p-8 rounded-3xl shadow-md border border-pink-50">
            <h3 className="font-bold text-gray-800 text-lg mb-6 flex items-center gap-2">
              <Award className="text-pink-500" /> St. Jude Donation Goals & Rules
            </h3>
            <form onSubmit={handleUpdateDonations} className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Donation Goal ($)</label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
                    <DollarSign size={16} />
                  </span>
                  <input
                    type="number"
                    step="50"
                    required
                    value={donationStats.goalAmount}
                    onChange={(e) => setDonationStats({ ...donationStats, goalAmount: e.target.value })}
                    className="w-full pl-8 pr-4 py-2.5 rounded-xl border border-gray-200 font-bold"
                  />
                </div>
                <p className="text-xs text-gray-400 mt-1">Set a fundraising target for the boutique progress widget.</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Donation Rate</label>
                <select
                  value={donationStats.donationPercentage}
                  onChange={(e) => setDonationStats({ ...donationStats, donationPercentage: e.target.value })}
                  className="w-full p-2.5 rounded-xl border border-gray-200 font-semibold"
                >
                  <option value={0.05}>5% of proceeds</option>
                  <option value={0.10}>10% of proceeds (Recommended)</option>
                  <option value={0.15}>15% of proceeds</option>
                  <option value={0.20}>20% of proceeds</option>
                  <option value={0.25}>25% of proceeds</option>
                </select>
                <p className="text-xs text-gray-400 mt-1">Adjust the percentage subtracted from each checkout to go to St. Jude.</p>
              </div>

              <button
                type="submit"
                className="w-full bg-pink-500 hover:bg-pink-600 text-white font-bold py-3 rounded-xl transition"
              >
                Save Settings
              </button>
            </form>
          </div>
        )}

      </div>
    </div>
  );
};

export default AdminDashboard;
